import React from 'react';

const RulesSummary = ({ rules }) => {
  if (!rules) return null;

  const getGradingTypeLabel = () => {
    switch (rules.gradingType) {
      case 'percentage': return 'Percentage System';
      case 'grade': return 'Letter Grades';
      case 'cgpa': return 'CGPA System';
      default: return 'Not Configured';
    }
  };

  const getGradingTypeIcon = () => {
    switch (rules.gradingType) {
      case 'percentage': return '📊';
      case 'grade': return '📝';
      case 'cgpa': return '⭐';
      default: return '❓';
    }
  };

  const getPassingStatus = () => {
    return rules.requireBothToPass 
      ? 'Must pass BOTH CIE and End Exam'
      : 'Aggregate score only (simple average)';
  };



  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">Current Configuration Summary</h3>
        <div className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
          Active
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Grading Type Card */}
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">{getGradingTypeIcon()}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">Grading</div>
          </div>
          <div className="font-bold text-gray-900 text-lg">{getGradingTypeLabel()}</div>
          <div className="text-xs text-gray-500 mt-1">
            {rules.gradingType === 'percentage' && `Distinction: ${rules.percentageConfig?.distinctionPercentage || 75}%+`}
            {rules.gradingType === 'cgpa' && `Scale: ${rules.cgpaConfig?.maxCGPA || 10}.0`}
            {rules.gradingType === 'grade' && `${rules.gradeThresholds?.length || 0} grade levels`}
          </div>
        </div>
        
        {/* Passing Criteria Card */}
        <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 border border-emerald-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">🎯</div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">Passing</div>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">CIE:</span>
              <span className={`font-bold ${rules.ciePassingPercentage > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                {rules.ciePassingPercentage || 0}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">End Exam:</span>
              <span className={`font-bold ${rules.endExamPassingPercentage > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                {rules.endExamPassingPercentage || 0}%
              </span>
            </div>
            <div className="flex justify-between border-t pt-1 mt-1">
              <span className="text-gray-600 font-medium">Overall:</span>
              <span className="font-bold text-emerald-700">{rules.overallPassingPercentage || 40}%</span>
            </div>
          </div>
        </div>
        
        {/* Assessment Method Card */}
        <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 border border-purple-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">⚖️</div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">Method</div>
          </div>
          <div className="font-semibold text-gray-900 text-sm">Simple Average</div>
          <div className="text-xs text-gray-500 mt-1">
            (CIE + End Exam) ÷ 2
          </div>
          <div className="mt-2 text-xs text-purple-600">
            {getPassingStatus()}
          </div>
        </div>
        
        {/* Rounding Policy Card */}
        <div className="bg-gradient-to-br from-pink-50 to-white rounded-xl p-4 border border-pink-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">🔢</div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">Rounding</div>
          </div>
          <div className="font-bold text-gray-900 text-sm">
            {(() => {
              switch (rules.roundingMode) {
                case 'nearestHalf': return 'Round to nearest 0.5';
                case 'nearestInt': return 'Round to nearest 1.0';
                case 'floor': return 'Always round down (floor)';
                case 'ceil': return 'Always round up (ceil)';
                default: return 'Round to nearest 0.5';
              }
            })()}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            <b>Examples:</b><br/>
            Student raw marks: <b>78.36</b> →
            {(() => {
              switch (rules.roundingMode) {
                case 'nearestHalf': return ' 78.5';
                case 'nearestInt': return ' 78';
                case 'floor': return ' 78';
                case 'ceil': return ' 79';
                default: return ' 78.5';
              }
            })()}<br/>
            Student raw marks: <b>78.7</b> →
            {(() => {
              switch (rules.roundingMode) {
                case 'nearestHalf': return ' 78.5';
                case 'nearestInt': return ' 79';
                case 'floor': return ' 78';
                case 'ceil': return ' 79';
                default: return ' 78.5';
              }
            })()}<br/>
            <b>Checkbox:</b> {rules.roundingMode !== 'none' ? 'Rounding enabled' : 'Rounding disabled'} (e.g., Accept Terms, Enable notifications)
          </div>
        </div>

        {/* Credit System Card */}
        <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-4 border border-amber-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-2xl">💳</div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">Credits</div>
          </div>
          {rules.academicSystem?.creditSystemEnabled ? (
            <>
              <div className="font-bold text-gray-900">
                {rules.academicSystem.minCredits} - {rules.academicSystem.maxCredits} credits/subject
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Total: {rules.academicSystem.totalDegreeCredits} credits
              </div>
            </>
          ) : (
            <div className="text-gray-500 text-sm">Credit System Disabled</div>
          )}
        </div>
      </div>
      
      {/* Grade Thresholds Section (only for grade/cgpa systems) */}
      {(rules.gradingType === 'grade' || rules.gradingType === 'cgpa') && rules.gradeThresholds?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-4">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-700">Grade Thresholds</span>
              <span className="text-xs text-gray-400">{rules.gradeThresholds.length} levels</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Grade</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Range</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Points</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rules.gradeThresholds.map((threshold, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-semibold text-gray-900">{threshold.grade}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {threshold.minPercentage}% - {threshold.maxPercentage}%
                    </td>
                    <td className="px-4 py-2 text-gray-600">{threshold.gradePoint}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        threshold.isPassing ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {threshold.isPassing ? 'Pass' : 'Fail'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Additional Info for Percentage System */}
      {rules.gradingType === 'percentage' && rules.percentageConfig?.distinctionPercentage && (
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center gap-3">
            <div className="text-2xl">🏆</div>
            <div>
              <div className="text-sm font-semibold text-green-800">Distinction/Honors</div>
              <div className="text-xs text-green-700">
                Students scoring {rules.percentageConfig.distinctionPercentage}% or above will be awarded Distinction
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Additional Info for CGPA System */}
      {rules.gradingType === 'cgpa' && rules.cgpaConfig && (
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="text-2xl">📊</div>
            <div>
              <div className="text-sm font-semibold text-blue-800">CGPA Information</div>
              <div className="text-xs text-blue-700">
                Scale: {rules.cgpaConfig.maxCGPA}.0 | Minimum Passing: {rules.cgpaConfig.passingCGPA} CGPA
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Stats Footer */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mt-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-xs text-gray-400">Last Updated</div>
            <div className="text-sm font-medium text-gray-700">
              {rules.updatedAt ? new Date(rules.updatedAt).toLocaleDateString() : 'Never'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Subject Types</div>
            <div className="text-sm font-medium text-gray-700">{rules.subjectTypes?.length || 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Attendance Required</div>
            <div className="text-sm font-medium text-gray-700">
              {rules.attendancePolicy?.enabled ? `${rules.attendancePolicy.defaultMinimumPercentage || 75}%` : 'Disabled'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Auto-block</div>
            <div className="text-sm font-medium text-gray-700">
              {rules.attendancePolicy?.autoblock ? `After ${rules.attendancePolicy.blockAfterDays} days` : 'Off'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RulesSummary;